import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { Checkbox, Dropdown, PrimaryButton, TextField } from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import * as React from 'react';
import { Context as ReactContext, useContext, useEffect } from 'react';

import { EntityType } from '../../../Common/DataOwnerModels/CDMRecord';
import { IMachineLearningExperiment } from '../../../Common/MachineLearningModels/IMachineLearningExperiment';
import * as FormStyle from '../../../Common/Styles/form.styled';
import { useGetAllDataOwners } from '../../../Hooks/DataOwner/useGetAllDataOwners';
import { MachineLearningEntityTypeSelector } from './MachineLearningEntityTypeSelector';

export function AddMachineLearningExperimentPanel(props: {
  isOpen: boolean;
  dismissPanel(): void;
  experiment: IMachineLearningExperiment;
}): React.ReactElement {
  const { httpClient } = useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const [machineLearningExperiment, setMachineLearningExperiment] = React.useState<IMachineLearningExperiment>(
    props.experiment ?? ({} as IMachineLearningExperiment)
  );
  const [entityTypes, setEntityTypes] = React.useState<EntityType[]>([]);
  const [dataOwners] = useGetAllDataOwners(false);

  const handleSaveButtonPressed = () => {
    httpClient
      .post(`${__API_BASE_URL__}/machine-learning/experiments`, {
        resource: __CLIENT_ID__,
        data: machineLearningExperiment,
      })
      .then(() => {
        props.dismissPanel();
      });
  };

  React.useEffect(() => {
    if (machineLearningExperiment.dataOwnerId == null) return;
    httpClient
      .get<EntityType[]>(
        `${__API_BASE_URL__}/tenant/getApprovedCDMsByTenant?tenantId=${machineLearningExperiment.dataOwnerId}&environment=1`,
        {
          resource: __CLIENT_ID__,
        }
      )
      .then(({ data }) => {
        const sortedCdmList = (data as EntityType[]).sort((a: EntityType, b: EntityType) => {
          return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
        });

        for (const entityType of sortedCdmList) {
          const schema = JSON.parse(entityType.schema);

          let firstLevelProps = {};
          if (schema['type'] == 'array') firstLevelProps = schema['items']['properties'];
          else if (schema['type'] == 'object') firstLevelProps = schema['properties'];
          else firstLevelProps = {};
          const firstLevelPropsArray = Object.keys(firstLevelProps);
          entityType.properties = firstLevelPropsArray.sort((a, b) => a.localeCompare(b));
        }

        setEntityTypes(sortedCdmList);
      });
  }, [machineLearningExperiment.dataOwnerId]);

  useEffect(() => {
    if (props.experiment === null) return;

    setMachineLearningExperiment(props.experiment);
  }, [props.experiment]);

  return (
    <CoherencePanel
      isOpen={props.isOpen}
      // onRenderFooter={onRenderFooterContent}
      onDismiss={props.dismissPanel}
      // onRenderHeader={onRenderTitleText}
      panelSize={CoherencePanelSize.xlarge}
      closeButtonAriaLabel="Close"
    >
      <FormStyle.InputGroup>
        <h2>New machine learning project</h2>
        <p>
          Use this form to create a new machine learning projects. Once created, the projects cannot be edited, but can
          be disabled.
        </p>
      </FormStyle.InputGroup>

      <FormStyle.InputGroup>
        <Checkbox
          label="Enabled"
          checked={!machineLearningExperiment.disabled}
          onChange={(e, checked) => {
            setMachineLearningExperiment({
              ...machineLearningExperiment,
              disabled: !checked,
            });
          }}
        />
      </FormStyle.InputGroup>

      <Body>
        <FormStyle.InputGroup>
          <Dropdown
            disabled={props.experiment !== null}
            label="Data owner"
            options={(dataOwners || []).map((dataOwner) => ({
              key: dataOwner.id,
              text: dataOwner.name,
            }))}
            placeholder="Select a data owner"
            onChange={(e, option) => {
              setMachineLearningExperiment({
                ...machineLearningExperiment,
                dataOwnerId: option.key.toString(),
              });
            }}
            selectedKey={machineLearningExperiment.dataOwnerId}
          />
        </FormStyle.InputGroup>

        <FormStyle.InputGroup>
          <Dropdown
            disabled={props.experiment !== null}
            label="Machine learning project type"
            options={[
              {
                key: 'Data Anomaly Detection',
                text: 'Data Anomaly Detection',
              },
              {
                key: 'Time-series Forecasting',
                text: 'Time-series Forecasting',
                disabled: true,
              },
            ]}
            selectedKey={machineLearningExperiment.type}
            onChange={(e, option) => {
              setMachineLearningExperiment({
                ...machineLearningExperiment,
                type: option.key.toString(),
              });
            }}
            placeholder="Select machine learning project type"
          />
        </FormStyle.InputGroup>

        <FormStyle.InputGroup>
          <TextField
            label="Name"
            id="name"
            required={true}
            value={machineLearningExperiment.name}
            onChange={(e, value) =>
              setMachineLearningExperiment({
                ...machineLearningExperiment,
                name: value,
              })
            }
            validateOnLoad={false}
            validateOnFocusOut={true}
          />
        </FormStyle.InputGroup>

        <FormStyle.InputGroup>
          <TextField
            label="Description"
            id="description"
            placeholder="Describe the purpose of the machine learning project"
            required={true}
            multiline={true}
            value={machineLearningExperiment.description}
            onChange={(e, value) =>
              setMachineLearningExperiment({
                ...machineLearningExperiment,
                description: value,
              })
            }
            validateOnLoad={false}
            validateOnFocusOut={true}
          />
        </FormStyle.InputGroup>
      </Body>

      <MachineLearningEntityTypeSelector
        entityTypes={entityTypes}
        value={machineLearningExperiment}
        onChange={(val) => setMachineLearningExperiment(val)}
        readonly
      />

      <PrimaryButton text="Save" onClick={handleSaveButtonPressed} />
    </CoherencePanel>
  );
}

const Body = styled.div`
  width: 50%;
`;
